/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from 'react';
import type {
  ReactNode,
  ReactElement,
  Ref,
} from 'react';

import type {
  GroupBase,
} from 'react-select';
import type {
  UseAsyncPaginateParams,
  ComponentProps,
} from 'react-select-async-paginate';

import { SelectBase } from '../SelectBase';

import {
  creatableTexts,
} from '../ReactSelectExtended/texts';
import {
  useStyles,
} from '../ReactSelectExtended/styles';
import { selectComponentsAsync } from '../ReactSelectExtended/components';
import type {
  OptionTypeBase,
  SelectBaseProps,
} from '../ReactSelectExtended/types';

import {
  CreatableAsyncPaginateExtended,
} from '../ReactSelectExtended/ReactSelectExtended';
import type {
  ReactSelectCreatableExtendedProps,
} from '../ReactSelectExtended/ReactSelectExtended';

export type AsyncCreatableProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
> =
  & ReactSelectCreatableExtendedProps<OptionType, IsMulti, Group>
  & UseAsyncPaginateParams<OptionType, Group, Additional>
  & ComponentProps<OptionType, Group, IsMulti>
  & Omit<SelectBaseProps<OptionType, Group, IsMulti>, 'defaultComponents' | 'children'>;

function AsyncCreatableComponent<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
>(
  props: AsyncCreatableProps<OptionType, Group, Additional, IsMulti>,
  ref: Ref<HTMLDivElement>,
): ReactElement {
  const {
    hasError = false,
    hasWarning = false,
    isClearable = true,
    isMulti = false,
    renderModal = undefined,
    selectRef = null,
    small = false,
    ...restProps
  } = props;

  const styles = useStyles(small);

  return (
    <SelectBase
      defaultComponents={selectComponentsAsync}
      {...props}
      ref={ref}
    >
      {({
        getOptionLabel,
        getOptionValue,
        components,
      }): ReactNode => (
        <CreatableAsyncPaginateExtended
          {...creatableTexts}
          isMulti={isMulti}
          hasError={hasError}
          hasWarning={hasWarning}
          small={small}
          closeMenuOnSelect={!isMulti}
          isArrowHidden={Boolean(renderModal)}
          components={components}
          styles={styles}
          {...restProps}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          isClearable={isClearable}
          selectRef={selectRef}
        />
      )}
    </SelectBase>
  );
}

export const AsyncCreatable = forwardRef(AsyncCreatableComponent) as <
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
Additional,
IsMulti extends boolean,
>(
  props: AsyncCreatableProps<OptionType, Group, Additional, IsMulti> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
